import { CITY_GRID, tileToWorld, ROAD_INDICES } from '../world/CityMap';
import { GRID_SIZE, TILE_SIZE } from '../config';

export interface SidewalkTile {
  row: number;
  col: number;
  /** Tile center world coords — where a standing NPC would be placed. */
  x: number;
  z: number;
  /** Curb position — half a tile toward the nearest road. This is where the taxi
   * actually needs to stop; placing markers at x/z would put them inside buildings. */
  curbX: number;
  curbZ: number;
  /** Unit vector from curb toward the road. An NPC on the sidewalk facing an
   * arriving taxi should look in this direction. */
  facingX: number;
  facingZ: number;
}

function isRoadTile(row: number, col: number): boolean {
  if (row < 0 || row >= GRID_SIZE || col < 0 || col >= GRID_SIZE) return false;
  return ROAD_INDICES.has(row) || ROAD_INDICES.has(col);
}

/** Returns the direction (unit deltaRow, deltaCol) toward the nearest road,
 * or null if this isn't a sidewalk tile. Priority N → S → W → E for stable picks. */
function roadDirection(row: number, col: number): [number, number] | null {
  if (isRoadTile(row, col)) return null;
  if (isRoadTile(row - 1, col)) return [-1, 0];
  if (isRoadTile(row + 1, col)) return [1, 0];
  if (isRoadTile(row, col - 1)) return [0, -1];
  if (isRoadTile(row, col + 1)) return [0, 1];
  return null;
}

function compute(): SidewalkTile[] {
  const out: SidewalkTile[] = [];
  const curbOffset = TILE_SIZE * 0.5;
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (!CITY_GRID[row][col]) continue;
      const dir = roadDirection(row, col);
      if (!dir) continue;
      const [dRow, dCol] = dir;
      const [x, z] = tileToWorld(row, col, TILE_SIZE);
      // tileToWorld maps col → x and row → z, so dCol maps to +/-x offset.
      const curbX = x + dCol * curbOffset;
      const curbZ = z + dRow * curbOffset;
      out.push({ row, col, x, z, curbX, curbZ, facingX: dCol, facingZ: dRow });
    }
  }
  return out;
}

export const SIDEWALK_TILES: SidewalkTile[] = compute();

if (SIDEWALK_TILES.length === 0) {
  throw new Error('No pickup sites — CityMap produced zero sidewalk tiles');
}

export interface PickOptions {
  minDistanceFrom?: { x: number; z: number };
  minTiles?: number;
  excludeRowCol?: { row: number; col: number };
}

export function pickRandomSite(rng: () => number, options: PickOptions = {}): SidewalkTile {
  const { minDistanceFrom, minTiles = 6, excludeRowCol } = options;
  const minWorld = minTiles * TILE_SIZE;
  for (let i = 0; i < 50; i++) {
    const t = SIDEWALK_TILES[Math.floor(rng() * SIDEWALK_TILES.length)];
    if (excludeRowCol && t.row === excludeRowCol.row && t.col === excludeRowCol.col) continue;
    if (minDistanceFrom) {
      const dx = t.x - minDistanceFrom.x;
      const dz = t.z - minDistanceFrom.z;
      if (Math.hypot(dx, dz) < minWorld) continue;
    }
    return t;
  }
  const pool = SIDEWALK_TILES.filter((t) => {
    if (excludeRowCol && t.row === excludeRowCol.row && t.col === excludeRowCol.col) return false;
    if (minDistanceFrom) {
      const dx = t.x - minDistanceFrom.x;
      const dz = t.z - minDistanceFrom.z;
      if (Math.hypot(dx, dz) < minWorld) return false;
    }
    return true;
  });
  if (pool.length === 0) throw new Error('pickRandomSite: no tile matches filters');
  return pool[Math.floor(rng() * pool.length)];
}
